/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';
import classnames from 'classnames';
import { change } from 'redux-form';
import { connect } from 'react-redux';
import { Icon, Link, Modal } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { getFormValues } from 'redux-form';
import * as sass from 'sass';
import { v4 } from 'uuid';
import { QuestionMarkMajor } from '@shopify/polaris-icons';

import { CodeEditorComponent } from './code-editor-component';
import { Switch } from 'src/components/views/common/switch';
import { templateRender, templateBuilder, formatTemplate } from 'boost-template-engine';
import { combineWidgets, calculateDocSize } from 'src/helpers/ssr-helper';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { DEVICE } from 'src/constants/theme-settings';
import { getShop } from 'src/helpers';
import EditCode from 'src/assets/images/shopify-integration-settings/edit-file.png';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { KEY_EDITOR_STATE, MAPPING_AVAILABLE_TEMPLATE } from 'src/constants/shopify-integration';
import { URL } from 'src/constants/links';

type PropsFromState = {
  instantWidget: anu,
  widgetSettings: any,
  scssContent: string
};

type PropsFromDispatch = {};

type ComposedProps = PropsFromState & PropsFromDispatch & WithI18nProps;

class CodeEditor extends Component<ComposedProps, State> {
  state = {
    isShowModalReset: false
  };
  initializedPreview = false;
  compiledSettingsCss = '';
  originalCompiledSizes = 0;
  originalRenderedSizes = 0;

  componentDidMount() {
    const { widgetSettings, scssContent, instantWidget, selectedPage } = this.props;

    // first time compile scss from scss content and widget settings
    if (
      !_.isEmpty(widgetSettings) &&
      !_.isEmpty(scssContent) &&
      _.isEmpty(this.compiledSettingsCss)
    ) {
      this.compileScss(widgetSettings, scssContent);
    }

    // get selected widget from instance template
    if (
      !_.isEmpty(instantWidget) &&
      !_.isEmpty(instantWidget.widgets) &&
      _.isEmpty(this.selectedWidget)
    ) {
      const mainWidgetId = _.get(
        instantWidget,
        `widgetIds.${[MAPPING_AVAILABLE_TEMPLATE[selectedPage?.value]]}`
      );

      const selectedWidget =
        instantWidget.widgets[mainWidgetId] || Object.values(instantWidget.widgets)[0];

      this.selectedWidget = selectedWidget;
    }

    Emitter.on(EVENT_KEY.SAVE_EDITOR_STATE, this.handleSaveEditorState);
  }

  componentWillReceiveProps(nextProps) {
    const { customizeTemplate } = this.props;
    const { customizeTemplate: nextCustomizeTemplate, codeEditTemplate, editorModel } = nextProps;
    if (customizeTemplate !== nextCustomizeTemplate && this.editorRef && this.monacoRef) {
      let model = editorModel[codeEditTemplate?.widgetId];
      if (!model) {
        model = this.monacoRef.editor.createModel(nextCustomizeTemplate, 'html');
        this.handleSaveEditorModel(model);
      }
      this.editorRef.setModel(model);

      const editorState = this.getEditorStateFromLocalStorage();
      this.editorRef.restoreViewState(editorState[codeEditTemplate?.widgetId] || {});
      this.editorRef.focus();
    }
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.SAVE_EDITOR_STATE, this.handleSaveEditorState);
  }

  getAllWidgets = props => {
    const { formValues } = props || this.props;
    const instantWidgetFormValues = _.get(formValues, 'instantWidget', {});
    const widgets = _.get(instantWidgetFormValues, 'widgets', {});

    const allWidgets = Object.values(widgets).reduce((cur, next) => {
      return [...cur, ...next];
    }, []);
    return allWidgets;
  };

  getEditorStateFromLocalStorage = () => {
    let editorState = window.localStorage.getItem(`${getShop()}-${KEY_EDITOR_STATE}`) || '{}';
    editorState = JSON.parse(editorState);
    return editorState;
  };

  handleSaveEditorState = () => {
    if (!this.editorRef) {
      return;
    }

    const { codeEditTemplate } = this.props;
    const editorState = this.getEditorStateFromLocalStorage();
    window.localStorage.setItem(
      `${getShop()}-${KEY_EDITOR_STATE}`,
      JSON.stringify({
        ...editorState,
        [codeEditTemplate?.widgetId]: this.editorRef.saveViewState()
      })
    );
  };

  handleEditorDidMount = (editor, monaco) => {
    // here is the editor instance
    this.editorRef = editor;
    this.monacoRef = monaco;
    this.props.onUpdateModel(this.editorRef.getModel());
  };

  handleSaveEditorModel = model => {
    this.props.onUpdateModel(model);
  };

  compileScss = async (widgetSettings, scssContent) => {
    if (!_.isEmpty(widgetSettings) && !_.isEmpty(scssContent)) {
      const sassMapString = `$settings: (${this.drill(widgetSettings)});\n`;
      const compiledScss = sassMapString + '\n' + scssContent;
      const { css } = sass.compileString(compiledScss);

      this.compiledSettingsCss = css;
      this.compileMainTemplate();
    }
  };

  getPreviewData = async props => {
    const { previewData, updateFieldReducer } = props || this.props;
    if (_.isEmpty(previewData)) {
      const host = process.env.REACT_APP_FILTER_URL || 'https://staging.bc-solutions.net';
      const filterUrl = host + '/bc-sf-filter/filter';
      const params = this.getRequestParams();
      const response = await fetch(`${filterUrl}?${new URLSearchParams(params)}`, {
        method: 'GET'
      });
      const previewData = await response.json();
      updateFieldReducer('previewData', previewData);
    }
    this.compileMainTemplate();
  };

  getRequestParams = props => {
    const { selectedPage, device, formValues } = props || this.props;
    const limitPerPage = _.get(formValues, `setting.${MENU_ID.PRODUCT_LIST}.productsPerPage`, 16);
    const productsPerRowOnDesktop = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnDesktop`,
      3
    );
    const productsPerRowOnMobile = _.get(
      formValues,
      `setting.${MENU_ID.PRODUCT_LIST}.productsPerRowOnMobile`,
      2
    );

    const data = {
      t: Date.now(),
      sid: v4(),
      _: 'pf',
      shop: getShop(),
      page: 1,
      sort: 'Relevance',
      locale: 'en',
      event_type: 'init',
      build_filter_tree: 'true',
      handle: 'all',
      previewMode: true,
      pg: selectedPage?.value,
      limit: limitPerPage,
      viewAs: `grid--${
        device === DEVICE.DESKTOP ? productsPerRowOnDesktop : productsPerRowOnMobile
      }`
    };

    if (device === DEVICE.MOBILE) {
      data.device = 'mobile, tabletPortraitMax';
    }
    return data;
  };

  /**
   * compile & render preview template
   */
  compileMainTemplate = async () => {
    const { widgetSettings, previewData, themeNameLib, themeLibVersion } = this.props;
    const allWidgets = this.getAllWidgets();

    if (
      !_.isEmpty(this.selectedWidget) &&
      !_.isEmpty(allWidgets) &&
      !_.isEmpty(widgetSettings) &&
      !_.isEmpty(this.compiledSettingsCss) &&
      !_.isEmpty(previewData)
    ) {
      const compiledTemplate = await combineWidgets(
        this.selectedWidget[0]?.widgetName,
        allWidgets,
        widgetSettings,
        this.compiledSettingsCss,
        themeNameLib,
        themeLibVersion
      );
      // console.log('compiledTemplate', compiledTemplate); originalRenderedSizes
      const renderedTemplate = templateRender(compiledTemplate, {
        ...previewData,
        request: this.getRequestParams()
      });

      // set origin render size
      if (!this.originalRenderedSizes || this.originalRenderedSizes === 0) {
        this.originalRenderedSizes = calculateDocSize(renderedTemplate);
      }
      // set origin doc size
      if (!this.originalCompiledSizes || this.originalCompiledSizes === 0) {
        this.originalCompiledSizes = calculateDocSize(compiledTemplate);
      }
    }
  };

  drill = obj => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const buildValue = value => {
        if (!value) return `'${key}': inherit,`;
        if (value && typeof value === 'object') return `'${key}': (${this.drill(value)}),`;
        return `'${key}': ${JSON.stringify(String(value))},`;
      };
      acc += buildValue(value);
      return acc;
    }, '');
  };

  handleShowModalReset = () => {
    const { codeEditTemplate, templatesEditing } = this.props;

    if (
      templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate !==
      templatesEditing[codeEditTemplate?.widgetId]?.customizeTemplate
    ) {
      this.setState({
        isShowModalReset: true
      });
    } else {
      // If code is not different from original, just turn off toggle
      this.updateWidget('hasCustomized', false);
    }
  };

  handleHideModalReset = () => {
    this.setState({
      isShowModalReset: false
    });
  };

  handleToggleEnableEdit = value => {
    // update customization flags from form value to selected editing widget-template
    this.setState({
      isShowModalReset: false
    });
    this.updateWidget('hasCustomized', value);
  };

  onChangeCode = async value => {
    // const {
    //   widgetSettings,
    //   previewData,
    //   updateFieldReducer,
    //   templatesEditing,
    //   codeEditTemplate
    // } = this.props;
    // const verifyCode = async value => {
    //   try {
    //     const detectLoop = detectLoops(value);
    //     console.log('detectLoop', detectLoop);
    //     const memoryBefore = window.performance?.memory?.usedJSHeapSize || 0;
    //     console.time('verifyCodeCustomization');

    //     // Start calculate performance usage for compile and render customizations
    //     await waitFor(200); // wait for fresh memory usage by react and another script
    //     const startCompileTime = new Date();
    //     // calculate compile template
    //     const compiledTemplate = await combineWidgets(
    //       'Filter tree and Product listing',
    //       this.selectedWidget,
    //       widgetSettings,
    //       this.compiledSettingsCss
    //     );
    //     const memoryAfterCompile = window.performance?.memory?.usedJSHeapSize || 0;
    //     const endCompileTime = new Date();
    //     const verifyRendered = templateRender(compiledTemplate, {
    //       ...previewData,
    //       request: this.getRequestParams()
    //     });
    //     const compiledSize = calculateDocSize(compiledTemplate);
    //     const customizationRenderedSize = calculateDocSize(verifyRendered);

    //     // log customization calculations info
    //     const endRenderTime = new Date();
    //     const timeCompileDiff = endCompileTime - startCompileTime;
    //     const timeRenderDiff = endRenderTime - endCompileTime;

    //     const memoryAfterRender = window.performance?.memory?.usedJSHeapSize || 0;

    //     const memoryCompileDiff = memoryAfterCompile - memoryBefore;
    //     const memoryRenderDiff = memoryAfterRender - memoryAfterCompile;

    //     console.log(`Template size:
    //       - Original compile size: ${bytesToMB(this.originalCompiledSizes)} MB
    //       - Customization compile size: ${bytesToMB(compiledSize)} MB
    //       - Original render size: ${bytesToMB(this.originalRenderedSizes)} MB
    //       - Customization size ${bytesToMB(customizationRenderedSize)} MB`);

    //     console.log(`Customization Template Memory
    //     - Before compile and render: ${memoryBefore} bytes,
    //     - After compile:  ${memoryAfterCompile} bytes
    //     - After render:  ${memoryAfterRender} bytes`);
    //     console.log(`Memory used:
    //       - Compile ${bytesToMB(memoryCompileDiff)} MB
    //       - Render ${bytesToMB(memoryRenderDiff)} MB`);
    //     console.log(`Time compile and render customization:
    //       - Compile: ${timeCompileDiff.toFixed(2)} milliseconds
    //       - Render: ${timeRenderDiff.toFixed(2)} milliseconds
    //       `);
    //     console.timeEnd('verifyCodeCustomization');
    //     // console.log(`template builded: ${verifyBuilded}`);
    //     // console.log(`template rendered: ${verifyRendered}`);
    //     return true;
    //   } catch (e) {
    //     console.log('Verify code failed ---------> ', e);
    //     return false;
    //   }
    // };

    // const compiledTemplate = await verifyCode(value);

    // if (compiledTemplate) {
    //   this.updateWidget('customizeTemplate', value);
    //   updateFieldReducer('templatesEditing', {
    //     ...templatesEditing,
    //     [codeEditTemplate?.widgetId]: {
    //       customizeTemplate: value,
    //       originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
    //       isEditing: !_.isEqual(
    //         templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
    //         value
    //       )
    //     }
    //   });
    //   this.compileMainTemplate();
    // } else {
    //   updateFieldReducer(
    //     'customizeTemplate',
    //     templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate
    //   );
    //   this.editorRef.trigger('Invalid template', 'undo');
    // }

    this.updateWidget('customizeTemplate', value);
    // this.compileMainTemplate();
  };

  updateWidget = (field, value) => {
    const {
      codeEditTemplate,
      instantWidget,
      templatesEditing,
      updateFieldReducer,
      updateMultiFieldsReducer
    } = this.props;
    const payloadTemplatesEditing = _.cloneDeep(templatesEditing);
    // codeEditTemplate?.widgetId for the parent element

    const rootKeys = [];
    const widgets = _.get(instantWidget, 'widgets', {});
    Object.keys(widgets).forEach(key => {
      const arrWidget = widgets[key];
      const isExistSharingWidget = arrWidget.find(
        w => w?.widgetName === codeEditTemplate?.widgetName
      );
      if (isExistSharingWidget) {
        rootKeys.push(key);
      }
    });

    if (!_.isEmpty(rootKeys)) {
      rootKeys.forEach(rootKey => {
        this.updateWidgetDetail(field, value, rootKey, payloadTemplatesEditing);
      });
    } else {
      const refId = codeEditTemplate?.widgetId;
      this.updateWidgetDetail(field, value, refId, payloadTemplatesEditing);
    }

    if (field === 'hasCustomized' && !value) {
      this.editorRef.setValue(templatesEditing[codeEditTemplate?.widgetId].originalTemplate);
      updateMultiFieldsReducer({
        templatesEditing: {
          ...payloadTemplatesEditing,
          [codeEditTemplate?.widgetId]: {
            // selected widget
            customizeTemplate: templatesEditing[codeEditTemplate?.widgetId].originalTemplate,
            originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            isEditing: false
          }
        },
        customizeTemplate: templatesEditing[codeEditTemplate?.widgetId].originalTemplate
      });
    } else if (field === 'customizeTemplate') {
      updateFieldReducer('templatesEditing', {
        ...payloadTemplatesEditing,
        [codeEditTemplate?.widgetId]: {
          customizeTemplate: value,
          originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
          isEditing: !_.isEqual(
            templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            value
          )
        }
      });
    }
  };

  updateWidgetDetail = (field, value, rootKey, payloadTemplatesEditing) => {
    const { codeEditTemplate, updateField, formValues, templatesEditing } = this.props;
    // codeEditTemplate?.widgetId for the parent element
    const instantWidgetFormValues = _.get(formValues, 'instantWidget', {});

    const refWidget = _.cloneDeep(_.get(instantWidgetFormValues, `widgets.${rootKey}`, []));
    refWidget.forEach(widget => {
      if (widget?.widgetName === codeEditTemplate?.widgetName) {
        _.set(widget, field, value);

        if (field === 'hasCustomized' && !value) {
          _.set(
            widget,
            'customizeTemplate',
            templatesEditing[codeEditTemplate?.widgetId].originalTemplate
          );

          // trace widgets with the same widget name to edit
          payloadTemplatesEditing[widget?.widgetId] = {
            customizeTemplate: templatesEditing[codeEditTemplate?.widgetId].originalTemplate,
            originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            isEditing: false
          };
        } else if (field === 'customizeTemplate') {
          // edit other sharing widget code
          payloadTemplatesEditing[widget?.widgetId] = {
            customizeTemplate: value,
            originalTemplate: templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
            isEditing: !_.isEqual(
              templatesEditing[codeEditTemplate?.widgetId]?.originalTemplate,
              value
            )
          };
        }
      }
    });

    updateField(`instantWidget.widgets.${rootKey}`, refWidget);
  };

  render() {
    const {
      i18n,
      isExpandFullscreen,
      activeMenuItem,
      instantWidget: instantWidgetReducer,
      widgetSettings,
      scssContent,
      codeEditTemplate,
      customizeTemplate
    } = this.props;

    const previewClass = classnames('visual-editor-code', {
      full: isExpandFullscreen,
      'overflow-hidden':
        activeMenuItem === MENU_ID.QUICK_VIEW || activeMenuItem === MENU_ID.AJAX_CART
    });

    if (_.isEmpty(instantWidgetReducer) || _.isEmpty(widgetSettings) || _.isEmpty(scssContent)) {
      return null;
    }

    const originCompiledTemplate = !_.isEmpty(codeEditTemplate.widgetTemplate)
      ? formatTemplate(templateBuilder(codeEditTemplate.widgetTemplate, widgetSettings, {}, true))
      : '';

    const allWidgets = this.getAllWidgets();
    const isEditable = Boolean(
      allWidgets.find(item => item?.widgetId === codeEditTemplate?.widgetId)?.hasCustomized
    );

    return (
      <div className={previewClass}>
        {!_.isEmpty(codeEditTemplate) ? (
          <div className="visual-editor-code__container">
            <div className="visual-editor-code__col">
              <div>
                <p style={{ marginBottom: '8px' }}>
                  {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.compileTemplate')}
                </p>
              </div>
              <CodeEditorComponent
                language={'html'}
                value={originCompiledTemplate}
                options={{ readOnly: true }}
              />
            </div>

            <div className="visual-editor-code__col">
              <div className="visual-editor-code__btn-enable">
                <Switch
                  id="enable-code"
                  size="small"
                  value={isEditable}
                  onChange={isEditable ? this.handleShowModalReset : this.handleToggleEnableEdit}
                  label={i18n.translate(
                    'AdminHTML.ShopifyIntegrationSettings.Developer.overrideStyling'
                  )}
                />

                <span
                  className="icon-question-edit"
                  data-for="tooltip-enable-edit"
                  data-tip="tooltip-enable-edit">
                  <Icon source={QuestionMarkMajor} color="base" />
                </span>
              </div>

              <ReactTooltip
                id="tooltip-enable-edit"
                place="top"
                effect="solid"
                type="light"
                delayHide={200}
                delayUpdate={200}
                getContent={dataTip => {
                  return (
                    <p>
                      <span>
                        {i18n.translate(
                          'AdminHTML.ShopifyIntegrationSettings.Developer.viewHelpDocs'
                        )}
                      </span>{' '}
                      <Link url={URL['ShopifyIntegrationSettingsCodeEditor']} external>
                        {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.clickHere2')}
                      </Link>
                    </p>
                  );
                }}
              />

              <CodeEditorComponent
                language={'html'}
                value={customizeTemplate}
                onChange={value => this.onChangeCode(value)}
                disabled={!isEditable}
                onMount={this.handleEditorDidMount}
              />
            </div>
          </div>
        ) : (
          <div className="visual-editor-code__empty">
            <img src={EditCode} alt="edit-code" />
            <p className="title">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.editCode_Title')}
            </p>
            <p className="subtitle">
              {i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.editCode_SubTitle')}
            </p>
          </div>
        )}

        <Modal
          open={this.state.isShowModalReset}
          onClose={this.handleHideModalReset}
          title={i18n.translate('AdminHTML.ShopifyIntegrationSettings.Developer.resetCode_Title')}
          primaryAction={{
            content: i18n.translate('AdminHTML.ShopifyIntegrationSettings.Common.discard'),
            onAction: () => this.handleToggleEnableEdit(false),
            destructive: true
          }}
          secondaryActions={[
            {
              content: i18n.translate('AdminHTML.Common.Action.cancel'),
              onAction: this.handleHideModalReset
            }
          ]}>
          <Modal.Section>
            <div className="BC-modal-confirm-reset-code">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Developer.resetCode_Description'
              )}
            </div>
          </Modal.Section>
        </Modal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) =>
    dispatch(change(`ShopifyIntegrationSettingsVisualEditorForm`, field, value)),
  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateMultiFieldsReducer: data =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateMultiFields(data))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('ShopifyIntegrationSettingsVisualEditorForm')(state) || {};
  const instantWidget = _.get(state, 'templateInstance.templateInstance', {});
  const widgetSettings = _.get(state, 'themeIntegration.widgetSettings', {});
  const scssContent = _.get(state, 'themeIntegration.scssContent', '');

  const isExpandFullscreen =
    _.get(state, 'shopifyIntegrationSettings.device') === DEVICE.FULLSCREEN;

  return {
    formValues,
    instantWidget,
    widgetSettings,
    scssContent,
    isExpandFullscreen,
    activeMenuItem: _.get(state, 'shopifyIntegrationSettings.activeMenuItem', ''),
    themeName: _.get(state, 'shopifyIntegrationSettings.themeName', ''),
    selectedPage: _.get(state, 'shopifyIntegrationSettings.selectedPage', ''),
    device: _.get(state, 'shopifyIntegrationSettings.device'),
    enableCustomization: _.get(state, 'shopifyIntegrationSettings.enableCustomization', false),
    codeEditTemplate: _.get(state, 'shopifyIntegrationSettings.codeEditTemplate', {}),
    customizeTemplate: _.get(state, 'shopifyIntegrationSettings.customizeTemplate', ''),
    previewData: _.get(state, 'shopifyIntegrationSettings.previewData', {}),
    templatesEditing: _.get(state, 'shopifyIntegrationSettings.templatesEditing', {}),
    themeNameLib: _.get(state, 'templateInstance.templateInstance.themeNameLib', ''),
    themeLibVersion: _.get(state, 'templateInstance.templateInstance.themeLibVersion', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(CodeEditor));
